import { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Topbar from "./pages/global/Topbar";
import Sidebar from "./pages/global/Sidebar";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";

// Core pages
import Dashboard from "./pages/dashboard";
import SalesDashboard from "./pages/dashboard/SalesDashboard";

// Warehouse
import CreateWarehouse from "./pages/warehouse/CreateWarehouse";
import ViewWarehouse from "./pages/warehouse/ViewWarehouse";

// Customer
import CreateCustomer from "./pages/customer/CreateCustomer";
import ViewCustomer from "./pages/customer/ViewCustomer";

// Category
import CreateCategory from "./pages/category/CreateCategory";
import ViewCategory from "./pages/category/ViewCategory";

// Item
import CreateItem from "./pages/item/CreateItem";
import ViewItem from "./pages/item/ViewItem";

// Balance
// import CreateBalance from "./pages/balance/CreateBalance";
// import ViewBalance from "./pages/balance/ViewBalance";

//sales
import CreateSales from "./pages/sales/CreateSales";
import ViewSales from "./pages/sales/ViewSales";
import EditSales from "./pages/sales/EditSales";
import MySales from "./pages/sales/MySales";
import SalesRedirect from "./pages/sales/salesRedirect";
//lending
import CreateLending from "./pages/lending/CreateLending";
import ViewLending from "./pages/lending/ViewLending";

//stockout
import CreateStockOut from "./pages/stockout/CreateStockOut";
import ViewStockOut from "./pages/stockout/ViewStockOut";
//return
import CreateReturn from "./pages/return/CreateReturn";
import ViewReturn from "./pages/return/ViewReturn";

// Store
import CreateStore from "./pages/store/CreateStore";
import ViewStore from "./pages/store/ViewStore";

// Expence
import CreateExpence from "./pages/expence/CreateExpence";
import ViewExpence from "./pages/expence/ViewExpence";

// Car Information
import CreateCar from "./pages/car information/CreateCar";
import ViewCar from "./pages/car information/ViewCar";

// Car Operation
import CreateCarOperation from "./pages/car operation/CreateCarOperation";
import ViewCarOperation from "./pages/car operation/ViewCarOperation";

// Purchase
import CreatePurchase from "./pages/purchase/CreatePurchase";
import ViewPurchase from "./pages/purchase/ViewPurchase";
import EditPurchase from "./pages/purchase/EditPurchase";

// Role / Permission / User
import CreateRole from "./pages/role/CreateRole";
import ViewRole from "./pages/role/ViewRole";
import CreatePermission from "./pages/permission/CreatePermission";
import ViewPermission from "./pages/permission/ViewPermission";
import CreateUser from "./pages/users/CreateUser";
import ViewUser from "./pages/users/ViewUser";

// Auth
import Login from "./pages/userLogin";
import PurchaseReceipt from "./components/PurchaseReceipt";

//Reports
import ExpenseReport from "./pages/reports/expenseReport";
import SalesReport from "./pages/reports/salesReport";
import PurchaseReport from "./pages/reports/purchaseReport";
import LendingReport from "./pages/reports/lendingReport";
import ReturnReport from "./pages/reports/returnReport";
import StockoutReport from "./pages/reports/stockOutReport";
import IncomeReport from "./pages/reports/incomeReport";
import ProfitLossReport from "./pages/reports/profitReport";

import CreateBalanceSheet from "./pages/balanceSheet/createBalanceSheet";
import BalanceSheetTable from "./pages/balanceSheet/viewBalanceSheet";
import BalanceSheetForm from "./pages/balanceSheet/editBalance";

import RegisterFsMachine from "./pages/fsMachine/createFs";
import MachineFsList from "./pages/fsMachine/viewFs";
import CreateZBalance from "./pages/z balance/CreateZBalance";
import ViewZBalance from "./pages/z balance/ViewZBalance";

import RegisterCompany from "./pages/company/RegisterCompany";

// Permission-aware ProtectedRoute
const ProtectedRoute = ({ children, module, action }) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/register-company" replace />;

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const permissions = JSON.parse(localStorage.getItem("permissions") || "null");

  // Admin shortcut: full access
  if (user?.roleName?.toLowerCase() === "admin") return children;

  // Check permissions for non-admins when specified
  if (module && action) {
    if (!permissions?.[module]?.includes(action)) {
      return <Navigate to="/" replace />;
    }
  }
  return children;
};

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Public */}
          <Route path="/register-company" element={<RegisterCompany />} />
          <Route path="/login" element={<Login />} />

          {/* Protected Shell */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <div className="app">
                  <Sidebar
  isSidebar={isSidebar}
  setIsSidebar={setIsSidebar}
/>
                  <main className="content">
                    <Topbar setIsSidebar={setIsSidebar} />
                    <Routes>
                      {/* Core */}
                      <Route path="/" element={<Dashboard />} />
                      <Route
                        path="/sales-dashboard"
                        element={
                          <SalesDashboard
                            userId={
                              JSON.parse(localStorage.getItem("user"))?.id
                            }
                          />
                        }
                      />

                      {/* Warehouse */}
                      <Route
                        path="/warehouse/add"
                        element={
                          <ProtectedRoute module="Warehouse" action="create">
                            <CreateWarehouse />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/warehouse/view"
                        element={
                          <ProtectedRoute module="Warehouse" action="read">
                            <ViewWarehouse />
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer */}
                      <Route
                        path="/customer/add"
                        element={
                          <ProtectedRoute module="Customer" action="create">
                            <CreateCustomer />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/customer/view"
                        element={
                          <ProtectedRoute module="Customer" action="read">
                            <ViewCustomer />
                          </ProtectedRoute>
                        }
                      />

                      {/* Category */}
                      <Route
                        path="/category/add"
                        element={
                          <ProtectedRoute module="Category" action="create">
                            <CreateCategory />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/category/view"
                        element={
                          <ProtectedRoute module="Category" action="read">
                            <ViewCategory />
                          </ProtectedRoute>
                        }
                      />

                      {/* Item */}
                      <Route
                        path="/item/add"
                        element={
                          <ProtectedRoute module="Item" action="create">
                            <CreateItem />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/item/view"
                        element={
                          <ProtectedRoute module="Item" action="read">
                            <ViewItem />
                          </ProtectedRoute>
                        }
                      />

                      {/* Balance */}
                      {/* <Route
                        path="/balance/add"
                        element={
                          <ProtectedRoute module="Balance" action="create">
                            <CreateBalance />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/balance/view"
                        element={
                          <ProtectedRoute module="Balance" action="read">
                            <ViewBalance />
                          </ProtectedRoute>
                        }
                      /> */}
                      <Route
                        path="/balanceSheet/add"
                        element={
                          <ProtectedRoute
                            module="Balance Sheet"
                            action="create"
                          >
                            <CreateBalanceSheet />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/balanceSheet/edit/:id"
                        element={
                          <ProtectedRoute
                            module="Balance Sheet"
                            action="create"
                          >
                            <BalanceSheetForm />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/balanceSheet/view"
                        element={
                          <ProtectedRoute
                            module="Balance Sheet"
                            action="create"
                          >
                            <BalanceSheetTable />
                          </ProtectedRoute>
                        }
                      />

                      {/* Store */}
                      <Route
                        path="/store/add"
                        element={
                          <ProtectedRoute module="Store" action="create">
                            <CreateStore />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/store/view"
                        element={
                          <ProtectedRoute module="Store" action="read">
                            <ViewStore />
                          </ProtectedRoute>
                        }
                      />

                      {/* Expence */}
                      <Route
                        path="/expence/add"
                        element={
                          <ProtectedRoute module="Expence" action="create">
                            <CreateExpence />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/expence/view"
                        element={
                          <ProtectedRoute module="Expence" action="read">
                            <ViewExpence />
                          </ProtectedRoute>
                        }
                      />

                      {/* Car Information */}
                      <Route
                        path="/carinformation/add"
                        element={
                          <ProtectedRoute
                            module="Car Information"
                            action="create"
                          >
                            <CreateCar />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/carinformation/view"
                        element={
                          <ProtectedRoute
                            module="Car Information"
                            action="read"
                          >
                            <ViewCar />
                          </ProtectedRoute>
                        }
                      />

                      {/* Car Operation */}
                      <Route
                        path="/caroperation/add"
                        element={
                          <ProtectedRoute
                            module="Car Operation"
                            action="create"
                          >
                            <CreateCarOperation />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/caroperation/view"
                        element={
                          <ProtectedRoute module="Car Operation" action="read">
                            <ViewCarOperation />
                          </ProtectedRoute>
                        }
                      />

                      {/* Purchase */}
                      <Route
                        path="/purchase/add"
                        element={
                          <ProtectedRoute module="Purchase" action="create">
                            <CreatePurchase />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/purchase/view"
                        element={
                          <ProtectedRoute module="Purchase" action="read">
                            <ViewPurchase />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/purchase/edit/:id"
                        element={
                          <ProtectedRoute module="Purchase" action="read">
                            <EditPurchase />
                          </ProtectedRoute>
                        }
                      />
                      {/* receipt */}
                      <Route
                        path="/purchasereceipt"
                        element={<PurchaseReceipt />}
                      />

                      {/* Role / Permission / User */}
                      <Route
                        path="/role/add"
                        element={
                          <ProtectedRoute module="Role" action="create">
                            <CreateRole />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/role/view"
                        element={
                          <ProtectedRoute module="Role" action="read">
                            <ViewRole />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/permission/add"
                        element={
                          <ProtectedRoute module="Permission" action="create">
                            <CreatePermission />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/permission/view"
                        element={
                          <ProtectedRoute module="Permission" action="read">
                            <ViewPermission />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/user/add"
                        element={
                          <ProtectedRoute module="User" action="create">
                            <CreateUser />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/user/view"
                        element={
                          <ProtectedRoute module="User" action="read">
                            <ViewUser />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/sales/add"
                        element={
                          <ProtectedRoute module="Sales" action="create">
                            <CreateSales />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sales/view"
                        element={
                          <ProtectedRoute module="Sales" action="read">
                            <SalesRedirect />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sales/admin-view"
                        element={
                          <ProtectedRoute module="Sales" action="read">
                            <ViewSales />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sales/edit/:id"
                        element={
                          <ProtectedRoute module="Sales" action="read">
                            <EditSales />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sales/my-view"
                        element={
                          <ProtectedRoute module="Sales" action="read">
                            <MySales />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fs/add"
                        element={
                          <ProtectedRoute module="FS" action="create">
                            <RegisterFsMachine />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fs/view"
                        element={
                          <ProtectedRoute module="FS" action="read">
                            <MachineFsList />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/lending/add"
                        element={
                          <ProtectedRoute module="Lending" action="create">
                            <CreateLending />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/lending/view"
                        element={
                          <ProtectedRoute module="Lending" action="read">
                            <ViewLending />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/stockout/add"
                        element={
                          <ProtectedRoute module="Stock Out" action="read">
                            <CreateStockOut />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/stockout/view"
                        element={
                          <ProtectedRoute module="Stock Out" action="read">
                            <ViewStockOut />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/z balance/add"
                        element={
                          <ProtectedRoute module="Z Balance" action="read">
                            <CreateZBalance />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/z balance/view"
                        element={
                          <ProtectedRoute module="Z Balance" action="read">
                            <ViewZBalance />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/return/add"
                        element={
                          <ProtectedRoute module="Return" action="read">
                            <CreateReturn />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/return/view"
                        element={
                          <ProtectedRoute module="Return" action="read">
                            <ViewReturn />
                          </ProtectedRoute>
                        }
                      />
                      {/* <Route
                        path="/reports/expense"
                        element={
                          <ProtectedRoute module="Report" action="create">
                            <ExpenseReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/sales"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <SalesReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/purchase"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <PurchaseReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/lending"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <LendingReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/stockout"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <StockoutReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/return"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <ReturnReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/income"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <IncomeReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/profit"
                        element={
                          <ProtectedRoute module="Reports" action="create">
                            <ProfitLossReport />
                          </ProtectedRoute>
                        }
                      /> */}

                      <Route
                        path="/reports/expense"
                        element={
                          <ProtectedRoute module="Expense Report" action="read">
                            {" "}
                            {/* NEW */}
                            <ExpenseReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/sales"
                        element={
                          <ProtectedRoute module="Sales Report" action="read">
                            {" "}
                            {/* NEW */}
                            <SalesReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/purchase"
                        element={
                          <ProtectedRoute
                            module="Purchase Report"
                            action="read"
                          >
                            {" "}
                            {/* NEW */}
                            <PurchaseReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/lending"
                        element={
                          <ProtectedRoute module="Lending Report" action="read">
                            {" "}
                            {/* NEW */}
                            <LendingReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/return"
                        element={
                          <ProtectedRoute module="Return Report" action="read">
                            {" "}
                            {/* NEW */}
                            <ReturnReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/stockout"
                        element={
                          <ProtectedRoute
                            module="Stockout Report"
                            action="read"
                          >
                            {" "}
                            {/* NEW */}
                            <StockoutReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/income"
                        element={
                          <ProtectedRoute module="Income Report" action="read">
                            {" "}
                            {/* NEW */}
                            <IncomeReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reports/profit"
                        element={
                          <ProtectedRoute module="Profit Report" action="read">
                            {" "}
                            {/* NEW */}
                            <ProfitLossReport />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </main>
                </div>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
